type SecurityCheck<T> = (target?: T) => boolean;
interface AllowList { [functionName: string]: true }

/**
 * Creates a proxy object for the given target class. This proxy segregates the target's methods into two categories:
 *  - allowList methods: Defined by the allowList, these methods are always allowed to execute without any security check.
 *  - all other methods: Any method not specified in the allowList undergoes a security check at runtime. If the check fails, an error is thrown.
 *  An example use case is restricting access to certain methods unless a feature flag is active (or the method is explicitly allow-listed).
 */
export default class AllowListProxy<T extends object> {
    private readonly target: T;
    private readonly allowList: AllowList;
    private readonly securityCheck: SecurityCheck<T>;

    constructor(target: T, allowList: { [functionName: string]: true }, securityCheck: SecurityCheck<T>, validateAllowList = true) {
        this.target = target;
        this.securityCheck = securityCheck;
        this.allowList = allowList;
        validateAllowList && this.validateAllowList(allowList);
    }

    getProxiedTarget(): T {
        return new Proxy(this.target, this.getProxyHandler());
    }

    /* Validate that every function in the allowList actually exists on the target prototype. */
    private validateAllowList(allowList: AllowList): void {
        const functionsOnTarget: AllowList = Reflect.ownKeys(this.target.constructor.prototype)
            .filter(key => typeof this.target.constructor.prototype[key as string] === "function")
            .reduce((obj, key) => {
                obj[key as string] = true;
                return obj;
            }, {} as AllowList);
        Object.keys(allowList).forEach(key => {
            if (!functionsOnTarget[key]) {
                throw new Error(`Function ${ key } from the allowList is not present in the target class.`);
            }
        });
    }

    private getProxyHandler(): ProxyHandler<T> {
        return {
            get: (target, propKey, receiver) => {
                const propValue = Reflect.get(target, propKey, receiver);
                if (typeof propValue === "function" && !this.allowList[propKey as string]) {
                    return (...args: any[]) => {
                        if (!this.securityCheck(target)) {
                            throw new Error("Security check failed!");
                        }
                        return propValue.apply(target, args);
                    };
                }
                return propValue;
            }
        };
    }
}